import { BellOutlined } from '@ant-design/icons';
import { Card, Checkbox, Col, message, Row, Spin, Typography } from 'antd';
import {
  NotificationConfigOutput,
  useGetNotificationConfigListQuery,
  useUpdateNotificationConfigListMutation,
} from 'gql/types';
import { FC, useMemo, useState } from 'react';

const { Title, Text } = Typography;

/** Human-readable labels and descriptions for notification types */
const NOTIFICATION_META: Record<
  string,
  { label: string; description: string; group: string }
> = {
  // Operations notifications
  NewWooCommerceOrder: {
    label: 'New order received from WooCommerce',
    description: 'Get notified when a new order is placed on the website.',
    group: 'Operations',
  },
  DailyInventoryDigest: {
    label: 'Daily 7 AM inventory digest',
    description: 'Receive a daily summary of inventory levels each morning.',
    group: 'Operations',
  },
  InventoryWarningThreshold: {
    label: 'Inventory below warning threshold (25 POS)',
    description: 'Alert when stock at a location drops below 25 units.',
    group: 'Operations',
  },
  InventoryCriticalThreshold: {
    label: 'Inventory below critical threshold (10 POS)',
    description: 'Urgent alert when stock at a location drops below 10 units.',
    group: 'Operations',
  },
  PendingReservationConfirmation: {
    label: 'Pending reservation awaiting confirmation (v2)',
    description: 'Notification when a reservation needs to be confirmed.',
    group: 'Operations',
  },
  RecurringReservationReady: {
    label: 'Recurring reservation generated and ready (v2)',
    description:
      'Notification when a recurring reservation has been generated.',
    group: 'Operations',
  },
  HoldbackPoolBelowReservation: {
    label: 'Holdback pool drops below reservation requirement',
    description:
      'Alert when the holdback pool is insufficient for upcoming reservations.',
    group: 'Operations',
  },
  ReservationExpiringIn48Hours: {
    label: 'Reservation expiring within 48 hours (v2)',
    description: 'Reminder when a reservation is about to expire.',
    group: 'Operations',
  },
  // Existing notifications
  AppointmentsReminders: {
    label: 'Appointment reminders',
    description: 'Receive reminders about upcoming appointments.',
    group: 'Appointments',
  },
  AppointmentsCancellation: {
    label: 'Appointment cancellations',
    description: 'Get notified when an appointment is cancelled.',
    group: 'Appointments',
  },
  MakingAppointments: {
    label: 'New appointments',
    description: 'Get notified when new appointments are created.',
    group: 'Appointments',
  },
  RemindersToTakePartAgain: {
    label: 'Reminders to participate again',
    description: 'Receive reminders about donor eligibility.',
    group: 'Appointments',
  },
  DonorCreation: {
    label: 'Donor creation',
    description: 'Get notified when a new donor is created.',
    group: 'Donors',
  },
  RequestCreation: {
    label: 'Request creation',
    description: 'Get notified when a new request is submitted.',
    group: 'Donors',
  },
  OwnerCreation: {
    label: 'Owner creation',
    description: 'Get notified when a new owner is created.',
    group: 'Donors',
  },
  UserDeleting: {
    label: 'User deletion',
    description: 'Get notified when a user is deleted.',
    group: 'System',
  },
  DonorUnenrolled: {
    label: 'Donor unenrolled',
    description: 'Get notified when a donor is unenrolled.',
    group: 'Donors',
  },
  DaycareAppointmentRequestCreation: {
    label: 'Daycare appointment request',
    description: 'Get notified when a daycare appointment is requested.',
    group: 'Daycare',
  },
  DaycareAppointmentUnenrolled: {
    label: 'Daycare unenrollment',
    description: 'Get notified when a daycare appointment is unenrolled.',
    group: 'Daycare',
  },
  DaycareAppointmentRequestRejected: {
    label: 'Daycare request rejected',
    description: 'Get notified when a daycare request is rejected.',
    group: 'Daycare',
  },
  WasRegisteredOnDaycare: {
    label: 'Daycare registration',
    description: 'Get notified when registered on daycare.',
    group: 'Daycare',
  },
};

const GROUP_ORDER = [
  'Operations',
  'Donors',
  'Appointments',
  'Daycare',
  'System',
];

const NotificationSettings: FC = () => {
  const [notifications, setNotifications] = useState<
    NotificationConfigOutput[]
  >([]);

  const { loading } = useGetNotificationConfigListQuery({
    onCompleted: ({ getNotificationConfigList }) => {
      setNotifications(
        getNotificationConfigList.map((item) => {
          const { __typename, ...rest } = item;
          return rest as NotificationConfigOutput;
        })
      );
    },
  });

  const [updateNotifications] = useUpdateNotificationConfigListMutation({
    onError: (error) => message.error(error.message),
  });

  const handleToggle = (name: string, checked: boolean) => {
    const updated = notifications.map((n) =>
      n.name === name ? { ...n, value: checked } : n
    );
    setNotifications(updated);

    // Auto-save
    updateNotifications({
      variables: {
        input: {
          notifications: updated.map((n) => ({
            name: n.name,
            value: n.value,
          })),
        },
      },
    });
  };

  // Group notifications
  const grouped = useMemo(() => {
    const groups: Record<string, NotificationConfigOutput[]> = {};
    for (const n of notifications) {
      const meta = NOTIFICATION_META[n.name];
      const group = meta?.group || 'Other';
      if (!groups[group]) groups[group] = [];
      groups[group].push(n);
    }
    return groups;
  }, [notifications]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin />
      </div>
    );
  }

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Title level={4}>
          <BellOutlined style={{ marginRight: 8 }} />
          Notification Preferences
        </Title>
        <Text type="secondary">
          Choose which notifications you want to receive. Changes are saved
          automatically.
        </Text>
      </Col>

      {GROUP_ORDER.filter((g) => grouped[g]?.length).map((groupName) => (
        <Col span={24} key={groupName}>
          <Card
            size="small"
            title={
              <Text strong style={{ fontSize: 14 }}>
                {groupName}
              </Text>
            }
          >
            {grouped[groupName].map((n) => {
              const meta = NOTIFICATION_META[n.name];
              return (
                <div
                  key={n.name}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    padding: '10px 0',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                  <Checkbox
                    checked={n.value}
                    onChange={(e) => handleToggle(n.name, e.target.checked)}
                    style={{ marginTop: 2 }}
                  />
                  <div>
                    <div style={{ fontWeight: 500, fontSize: 13 }}>
                      {meta?.label || n.name}
                    </div>
                    {meta?.description && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {meta.description}
                      </Text>
                    )}
                  </div>
                </div>
              );
            })}
          </Card>
        </Col>
      ))}
    </Row>
  );
};

export default NotificationSettings;
